/**
 * HeaderBrandLogo — rendert das Site-Logo automatisch als <img> oder <video>,
 * basierend auf einem einzigen `media_id` aus `site_content['brand_logo']`.
 *
 * Kein Mode-Enum, keine Autoplay/Loop-Toggles im CMS. Die Video-Defaults
 * (autoplay, muted, loop, playsInline, Klick-Toggle, Route-Restart, Reduced-
 * Motion-Fallback, Error-Fallback) sind hier als Konstanten definiert – so
 * bleibt die Admin-UI minimal (nur ein Media-Picker).
 *
 * ## Media-Swap-Robustheit (Bug-Fix P0)
 *
 * `<video key={mediaId}>` erzwingt einen FRISCHEN DOM-Mount bei jedem Wechsel
 * der Media-ID. Ohne diesen Key reconciled React dasselbe `<video>`-Element
 * und ändert nur die `<source>`-Kinder — dann jedoch bleibt der Browser bei
 * den alt-gepufferten Streams (HTML5 lädt Sources nur beim initialen Attach
 * bzw. bei explizitem `.load()` neu). Symptom: neues Video blitzt kurz auf,
 * dann läuft das alte Video weiter oder es springt auf Fallback.
 *
 * Zusätzlich haben die Sources ein `key={mediaId + type}` und der onError-
 * Handler wechselt NICHT sofort in den img-Fallback wenn nur eine einzelne
 * `<source>` scheitert — nur wenn das gesamte `<video>` als kaputt gemeldet
 * wird. `onError` am `<video>` feuert nur wenn ALLE Sources fehlschlagen.
 *
 * ## Alpha-Transparenz (WebM VP9 mit Alpha)
 *
 * Wenn der Renderer eine Alpha-fähige Fassung erkennt (`webm_url` mit
 * VP9-Alpha), rendert er das `<video>` mit transparentem CSS-Hintergrund und
 * lässt so den Header-Untergrund durchscheinen. Safari (das VP9-Alpha vor
 * Version 16 nicht ganz sauber unterstützt) fällt automatisch auf die
 * optionale HEVC-MOV-Source zurück, sonst auf poster-Frame.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import ChromaKeyVideo, { type ChromaKeyConfig } from "@/components/ChromaKeyVideo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL as string;
const STATIC_FALLBACK = "/hypefly-logo-3d.png";

// Dezente Editorial-Kachel unter dem Logo. „Auf hauchdünnes Papier gelegt"-
// Effekt: sehr weicher Schatten + kaum sichtbarer warm-cremiger Grund
// (`--hf-surface` liegt genau einen Ton unter `--hf-bg`). Der Chroma-Key-
// Canvas hat transparente Ränder, deshalb wird die Rundung erst durch diese
// Wrapper-Fläche sichtbar. `overflow-hidden` clippt das innere Element sauber
// an den Rundungen.
const TILE_WRAPPER_BASE =
    "inline-flex items-center justify-center rounded-[10px] " +
    "overflow-hidden " +
    "bg-[hsl(var(--hf-surface))] " +
    "shadow-[0_2px_8px_rgba(23,22,19,0.06)] " +
    "border border-[hsl(var(--hf-line)/0.4)]";
// `object-cover` + `object-position: 50% 25%` → Kompromiss zwischen top
// (Propeller sichtbar) und center (Schriftzug sichtbar). 25 % Y-Offset
// bevorzugt den oberen Teil, ohne die untere Textzeile ganz zu opfern.
const TILE_INNER = "block w-full h-full object-cover object-[50%_25%] select-none";

// Hard-coded Video-Defaults (bewusst nicht im CMS konfigurierbar).
const VIDEO_AUTOPLAY = true;
const VIDEO_LOOP = true;
const VIDEO_MUTED = true;
const VIDEO_CLICK_TOGGLE = true;
const VIDEO_RESTART_ON_ROUTE = true;

type MediaInfo = {
    id: string;
    media_type?: string;
    mime?: string;
    poster_url?: string | null;
    webm_url?: string | null;
    hevc_mov_url?: string | null;
    /** Wenn `true` liefert `webm_url` eine VP9-Alpha-Variante → transparenter Header. */
    has_alpha?: boolean;
    /** Optional: Chroma-Key-Konfiguration → Live-Alpha-Berechnung im Browser. */
    chroma_key?: ChromaKeyConfig | null;
    title?: string;
};

interface Props {
    mediaId?: string | null;
    className?: string;
    testId?: string;
}

export default function HeaderBrandLogo({
    mediaId, className = "", testId = "site-header-logo-mark",
}: Props) {
    const location = useLocation();
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [info, setInfo] = useState<MediaInfo | null>(null);
    const [failed, setFailed] = useState(false);
    const [reducedMotion, setReducedMotion] = useState(false);

    // Reduced-Motion einmal beim Mount lesen.
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        setReducedMotion(mq.matches);
        const onChange = () => setReducedMotion(mq.matches);
        mq.addEventListener?.("change", onChange);
        return () => mq.removeEventListener?.("change", onChange);
    }, []);

    // Media-Info bei Wechsel der ID neu laden.
    useEffect(() => {
        setFailed(false);
        setInfo(null);
        if (!mediaId) return;
        let cancelled = false;
        void (async () => {
            try {
                const res = await fetch(`${BACKEND_URL}/api/media/${mediaId}/info`);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = (await res.json()) as MediaInfo;
                if (!cancelled) setInfo(data);
            } catch {
                if (!cancelled) setFailed(true);
            }
        })();
        return () => { cancelled = true; };
    }, [mediaId]);

    const isVideo = info?.media_type === "video" || info?.media_type === "video_360";
    const useVideo = !!mediaId && isVideo && !failed && !reducedMotion;

    // Route-Change → Video zurückspulen und weiterspielen.
    useEffect(() => {
        if (!useVideo || !VIDEO_RESTART_ON_ROUTE) return;
        const v = videoRef.current;
        if (!v) return;
        try {
            v.currentTime = 0;
            void v.play().catch(() => undefined);
        } catch {
            /* SecurityError bei Cross-Origin ignorieren */
        }
    }, [location.pathname, useVideo]);

    if (useVideo && mediaId) {
        // WICHTIG: Same-origin URLs verwenden. Backend liefert `webm_url`
        // wenn eine WebM-Sibling-Datei vorliegt — sonst rendern wir keine
        // WebM-Source, um vermeidbare 404s im Browser-Log zu sparen.
        const webmSrc = info?.webm_url
            ? `${BACKEND_URL}${info.webm_url}`
            : null;
        const hevcSrc = info?.hevc_mov_url
            ? `${BACKEND_URL}${info.hevc_mov_url}`
            : null;
        const mp4Src = `${BACKEND_URL}/api/media/${mediaId}`;
        const poster = info?.poster_url || `${BACKEND_URL}/api/media/${mediaId}/poster`;
        const hasAlpha = info?.has_alpha === true;

        // Chroma-Key-Modus: Media-Doc trägt eine `chroma_key`-Konfiguration.
        // Wir rendern das Video live durch einen WebGL-Fragment-Shader, der
        // den grünen Hintergrund transparent macht — ein Format, funktioniert
        // in jedem WebGL-fähigen Browser (Chrome/Firefox/Safari/iOS).
        //
        // WICHTIG: Chroma-Key braucht Same-Origin-Video (sonst wird das
        // Canvas nach `texImage2D` als "tainted" markiert und WebGL-Read
        // wirft SecurityError). Daher `/proxy`-Endpoint nutzen, nicht die
        // 302-Redirect-Route auf R2.
        if (info?.chroma_key && info.chroma_key.color) {
            const proxySrc = `${BACKEND_URL}/api/media/${mediaId}/proxy`;
            // WebM-Sibling (VP9): wichtig für Chromium-basierte Browser ohne
            // H.264-Lizenz (u.a. Playwright-Sandbox). Wenn vorhanden, wird der
            // Chroma-Key-Renderer VP9 bevorzugen und auf MP4 fallback nur wenn
            // das Encoding es erfordert.
            const proxyWebm = info.webm_url ? `${BACKEND_URL}${info.webm_url}` : null;
            return (
                <span
                    className={`${className} ${TILE_WRAPPER_BASE} cursor-pointer`}
                    data-testid={testId}
                    data-brand-logo-kind="chroma"
                >
                    <ChromaKeyVideo
                        key={`chroma-${mediaId}`}
                        src={proxySrc}
                        srcWebm={proxyWebm}
                        poster={poster}
                        chroma={info.chroma_key}
                        className={TILE_INNER}
                        testId={`${testId}-canvas`}
                        ariaLabel={info?.title || "HypeFly Logo Video"}
                        restartKey={location.pathname}
                        onError={() => setFailed(true)}
                    />
                </span>
            );
        }

        return (
            <span
                className={`${className} ${TILE_WRAPPER_BASE} ${VIDEO_CLICK_TOGGLE ? "cursor-pointer" : ""}`}
                data-testid={testId}
                data-brand-logo-kind="video"
                data-has-alpha={hasAlpha ? "true" : "false"}
            >
                <video
                    // Frischer Mount pro media_id: verhindert stale <source>-Buffer.
                    key={`video-${mediaId}`}
                    ref={videoRef}
                    poster={poster}
                    autoPlay={VIDEO_AUTOPLAY}
                    muted={VIDEO_MUTED}
                    loop={VIDEO_LOOP}
                    playsInline
                    controls={false}
                    preload="metadata"
                    onError={() => setFailed(true)}
                    onClick={() => {
                        if (!VIDEO_CLICK_TOGGLE) return;
                        const v = videoRef.current;
                        if (!v) return;
                        if (v.paused) void v.play().catch(() => undefined);
                        else v.pause();
                    }}
                    className={TILE_INNER}
                    style={hasAlpha ? { background: "transparent" } : undefined}
                    aria-label={info?.title || "HypeFly Logo Video"}
                >
                    {/* HEVC MOV (Safari/iOS mit Alpha) — falls Backend eine Alpha-
                        Variante bereitstellt, wird sie zuerst probiert. */}
                    {hevcSrc && (
                        <source src={hevcSrc} type='video/mp4; codecs="hvc1"' />
                    )}
                    {/* WebM VP9 (Chrome/Firefox/Edge, Alpha wenn vorhanden). */}
                    {webmSrc && (
                        <source src={webmSrc} type="video/webm" />
                    )}
                    {/* Klassisches H.264 MP4 als universeller Fallback (KEIN Alpha,
                        nur Rendering-Fallback für Legacy-Browser oder wenn keiner
                        der Alpha-Formate verfügbar ist). */}
                    <source src={mp4Src} type="video/mp4" />
                </video>
            </span>
        );
    }

    // Bild-Modus: entweder das gewählte Medium (media_id) oder statisches Fallback.
    // WICHTIG: Solange `info` noch pending ist, DÜRFEN wir nicht spekulativ
    // /api/media/{id} als <img>-src verwenden — es könnte ein Video sein und
    // würde onError feuern, was zu einem falschen `failed`-State führt. In
    // dieser Übergangsphase zeigen wir das statische PNG-Fallback (poster-
    // artig), bis der /info-Fetch die Art klärt.
    const infoLoaded = info !== null;
    const shouldUseMediaImg = !!mediaId && infoLoaded && !isVideo && !failed;
    const imgSrc = shouldUseMediaImg ? `${BACKEND_URL}/api/media/${mediaId}` : STATIC_FALLBACK;
    return (
        <span
            className={`${className} ${TILE_WRAPPER_BASE}`}
            data-testid={testId}
            data-brand-logo-kind={shouldUseMediaImg ? "image" : "fallback"}
        >
            <img
                // Frischer Mount pro media_id — konsistent mit dem <video>-Zweig.
                key={`img-${mediaId || "fallback"}`}
                src={imgSrc}
                alt="HypeFly – Premium 360°-Immobiliendokumentation"
                onError={(e) => {
                    const img = e.currentTarget;
                    if (img.src.indexOf(STATIC_FALLBACK) < 0) {
                        setFailed(true);
                        img.src = STATIC_FALLBACK;
                    }
                }}
                className={TILE_INNER}
            />
        </span>
    );
}
